import { type ReactNode, type RefObject, useId } from "react";

import { Box, Card, Flex, Heading } from "@radix-ui/themes";

import type {
  DataTableColumns,
  DataTableDensity,
} from "~/v3-poc/components/DataTable";
import { OrdersResultsPresentation } from "./OrdersResults.pres";
import type { OrdersWidgetRow } from "./OrdersWidget.types";
import type { ReorderOrdersColumns } from "./useOrdersColumnVisibility";
import "./OrdersWidget.table.css";

export type { OrdersWidgetCancellationFeedback } from "./OrdersCancellation.pres";

export interface OrdersWidgetPresentationProps {
  surface?: "standalone" | "workspace";
  filters: ReactNode;
  rows: OrdersWidgetRow[];
  columns: DataTableColumns<OrdersWidgetRow>;
  tableMinWidth: string;
  resultsRegionRef: RefObject<HTMLDivElement>;
  rowCount: number;
  selectedOrderId?: string;
  batchSelectedIds?: readonly string[];
  replacementFeedback: ReactNode;
  hasAppliedFilters: boolean;
  appliedTimeWindow: string;
  isAccountReady: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  isRefreshError: boolean;
  errorMessage?: string;
  hasNextPage: boolean;
  isFetchingNextPage?: boolean;
  onReorderColumns: ReorderOrdersColumns;
  density?: DataTableDensity;
  horizontalLines?: boolean;
  verticalLines?: boolean;
  onReset: () => void;
  onRetry: () => void;
  onLoadMore: () => void;
  onRowActivate: (
    row: OrdersWidgetRow,
    rowElement: HTMLTableRowElement,
  ) => void;
  replacePanel?: ReactNode;
  displayMenu: ReactNode;
  cancellationControls: ReactNode;
}

export const OrdersWidgetPresentation = ({
  surface = "standalone",
  filters,
  rows,
  columns,
  tableMinWidth,
  resultsRegionRef,
  rowCount,
  selectedOrderId,
  batchSelectedIds = [],
  replacementFeedback,
  hasAppliedFilters,
  appliedTimeWindow,
  isAccountReady,
  isLoading,
  isFetching,
  isError,
  isRefreshError,
  errorMessage,
  hasNextPage,
  isFetchingNextPage = false,
  onReorderColumns,
  density = "normal",
  horizontalLines = true,
  verticalLines = false,
  onReset,
  onRetry,
  onLoadMore,
  onRowActivate,
  replacePanel,
  displayMenu,
  cancellationControls,
}: OrdersWidgetPresentationProps) => {
  const pageContextId = useId();
  const isWorkspaceSurface = surface === "workspace";

  const content = (
    <Flex
      data-testid="orders-widget-content"
      data-orders-surface={surface}
      direction="column"
      gap="2"
      height={isWorkspaceSurface ? "100%" : undefined}
      minHeight={isWorkspaceSurface ? "0" : undefined}
      overflow={isWorkspaceSurface ? "hidden" : undefined}
    >
      <Box
        data-testid="orders-widget-toolbar"
        flexShrink="0"
        maxHeight={isWorkspaceSurface ? "55%" : undefined}
        overflow={isWorkspaceSurface ? "auto" : undefined}
      >
        <Flex direction="column" gap="2" p={isWorkspaceSurface ? "2" : "0"}>
          {isWorkspaceSurface ? null : (
            <Flex align="center" justify="between" gap="2">
              <Heading as="h2" size="4">
                Orders
              </Heading>
              {displayMenu}
            </Flex>
          )}
          {isWorkspaceSurface ? displayMenu : null}
          {filters}
        </Flex>
      </Box>

      {replacementFeedback}

      {cancellationControls}

      <OrdersResultsPresentation
        rows={rows}
        columns={columns}
        tableMinWidth={tableMinWidth}
        density={density}
        horizontalLines={horizontalLines}
        verticalLines={verticalLines}
        resultsRegionRef={resultsRegionRef}
        pageContextId={pageContextId}
        rowCount={rowCount}
        selectedOrderId={selectedOrderId}
        batchSelectedIds={batchSelectedIds}
        contained={isWorkspaceSurface}
        isAccountReady={isAccountReady}
        isLoading={isLoading}
        isFetching={isFetching}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        isError={isError}
        isRefreshError={isRefreshError}
        errorMessage={errorMessage}
        hasAppliedFilters={hasAppliedFilters}
        appliedTimeWindow={appliedTimeWindow}
        onReset={onReset}
        onRetry={onRetry}
        onLoadMore={onLoadMore}
        onRowActivate={onRowActivate}
        onReorderColumns={onReorderColumns}
      />
    </Flex>
  );

  return isWorkspaceSurface ? (
    <Box height="100%" minHeight="0" overflow="hidden">
      {replacePanel ? (
        <Flex height="100%" minHeight="0">
          <Box flexGrow="1" minWidth="0" minHeight="0" overflow="hidden">
            {content}
          </Box>
          {replacePanel}
        </Flex>
      ) : (
        content
      )}
    </Box>
  ) : (
    <Card size="2">
      {replacePanel ? (
        <Flex>
          <Box flexGrow="1" minWidth="0">
            {content}
          </Box>
          {replacePanel}
        </Flex>
      ) : (
        content
      )}
    </Card>
  );
};
